import { format } from 'date-fns';
import { memo } from 'react';

import { Button } from '@air/primitive-button';
import { ModalDescription } from '@air/primitive-modal';

import { CreditPackLineItem } from '~/components/Modals/ManageCreditPacks/CreditPackLineItem';
import { CreditPackModalWrapper } from '~/components/Modals/ManageCreditPacks/CreditPackModalWrapper';
import { useSubscriptionContext } from '~/providers/SubscriptionProvider';
import { useSubscriptionPlan } from '~/swr-hooks/subscriptions/useSubscriptionPlan';
import { formatMonthlyCredits } from '~/utils/creditUtils';
import { getSubscriptionInfo } from '~/utils/SubscriptionUtils';

export type ManageCreditPacksPendingChangesModalProps = {
  onClose: () => void;
  onContinue: () => void;
};

export const ManageCreditPacksPendingChangesModal = memo(
  ({ onClose, onContinue }: ManageCreditPacksPendingChangesModalProps) => {
    const { data: subscription } = useSubscriptionContext();
    const { data: subscriptionPlan } = useSubscriptionPlan();

    const title = 'Manage pending changes';

    const subscriptionInfo = getSubscriptionInfo({
      subscription,
      plan: subscriptionPlan?.plan,
      totalBillableSeats: 0,
    });

    if (!subscription?.pendingPackChange || !subscriptionInfo?.creditPack || !subscriptionInfo.billingCycle) {
      return (
        <CreditPackModalWrapper
          title={title}
          isLoading
          onClose={onClose}
          testId="MANAGE_CREDIT_PACKS_PENDING_CHANGES_MODAL"
        />
      );
    }

    const pending = subscription.pendingPackChange;
    const {
      billingCycle,
      planPriceCents,
      planCredits,
      creditPack,
      totalCredits: currentTotalCredits,
    } = subscriptionInfo;

    const pendingPackCredits = pending.targetQuantity * creditPack.size;
    const pendingPackPriceCents = pending.targetQuantity * creditPack.pricePerPackCents;
    const pendingTotalPriceCents = planPriceCents + pendingPackPriceCents;
    const effectiveDate = format(new Date(pending.effectiveAt), 'MMMM d, yyyy');

    return (
      <CreditPackModalWrapper title={title} onClose={onClose} testId="MANAGE_CREDIT_PACKS_PENDING_CHANGES_MODAL">
        <ModalDescription className="ads:text-metadata text-14 text-gray-10">
          Your current limit of {formatMonthlyCredits(currentTotalCredits)} will be available until the end of your
          billing cycle. After that, your card on file will be charged the new price starting on your next billing date.
        </ModalDescription>

        <div>
          <p className="mb-2 font-medium ads:text-primary text-10 text-gray-12 uppercase tracking-wider">
            Pending plan
          </p>
          <div className="flex flex-col gap-2 rounded-12 border ads:border-default border-gray-4 px-4 py-3">
            <CreditPackLineItem
              label={`${subscription.planName} Plan`}
              priceInCents={planPriceCents}
              billingCycle={billingCycle}
              description={formatMonthlyCredits(planCredits)}
            />

            {pending.targetQuantity > 0 ? (
              <CreditPackLineItem
                label="Credit pack"
                priceInCents={pendingPackPriceCents}
                billingCycle={billingCycle}
                description={formatMonthlyCredits(pendingPackCredits)}
              />
            ) : (
              <CreditPackLineItem
                label="Credit pack"
                priceInCents={creditPack.totalPriceCents}
                billingCycle={billingCycle}
                description={formatMonthlyCredits(creditPack.credits)}
                strikethrough
              />
            )}

            <CreditPackLineItem
              label="Updated price"
              priceInCents={pendingTotalPriceCents}
              billingCycle={billingCycle}
              description={`Next billing date ${effectiveDate}`}
              separator
            />
          </div>
        </div>

        <footer className="flex justify-between gap-2">
          <Button appearance="outline" color="gray" onClick={onClose} size="medium">
            Keep changes
          </Button>
          <Button appearance="filled" color="blue" onClick={onContinue} size="medium">
            Change credit pack
          </Button>
        </footer>
      </CreditPackModalWrapper>
    );
  },
);

ManageCreditPacksPendingChangesModal.displayName = 'ManageCreditPacksPendingChangesModal';
